import useThemeColors from '@/hooks/useThemeColors';
import { CalendarEvent } from '@/types';
import React from 'react';
import EventCard from './EventCard';

interface Props {
    events: CalendarEvent[];
    year: number;
    month: number;
    currentDate: string;
}

const CalendarGrid: React.FC<Props> = ({
    events,
    year,
    month,
    currentDate,
}) => {
    const colors = useThemeColors();
    const daysOfWeek = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        const firstDay = new Date(year, month - 1, 1).getDay();
        // Convert JS day index (Sun=0) to Monday-first index (Mon=0, Sun=6).
        return (firstDay + 6) % 7;
    };

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    // Parse currentDate string directly to avoid timezone issues
    const [todayYear, todayMonth, todayDay] = currentDate
        .split('-')
        .map(Number);

    const calendarDays: (number | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
        calendarDays.push(null);
    }

    // Add the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
        calendarDays.push(day);
    }

    const getEventsForDay = (day: number | null) => {
        if (day === null) return [];

        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return events.filter((event) => event.date === dateStr);
    };

    const isToday = (day: number | null) => {
        if (day === null) return false;
        return day === todayDay && month === todayMonth && year === todayYear;
    };

    const isWeekend = (index: number) => {
        const dayOfWeek = index % 7;
        return dayOfWeek === 5 || dayOfWeek === 6;
    };

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
            {/* Days of week header */}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-neutral-700">
                {daysOfWeek.map((day) => (
                    <div
                        key={day}
                        className="py-1.5 text-center text-[9px] font-medium tracking-wide text-gray-500 dark:text-neutral-400 uppercase sm:py-2 sm:text-[11px]"
                    >
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
                {calendarDays.map((day, index) => {
                    const dayEvents = getEventsForDay(day);
                    const isTodayDate = isToday(day);
                    const isWeekendDay = isWeekend(index);

                    return (
                        <div
                            key={index}
                            className={`min-h-[70px] border-r border-b border-gray-100 dark:border-neutral-700 p-1 last:border-r-0 sm:min-h-[90px] sm:p-1.5 ${
                                day === null ? 'bg-gray-50 dark:bg-neutral-900' : ''
                            } ${isTodayDate ? colors.accentBg : ''} ${
                                isWeekendDay && day !== null ? 'bg-gray-50 dark:bg-neutral-900' : ''
                            }`}
                        >
                            {day !== null ? (
                                <>
                                    <div className="mb-1 flex items-start justify-between sm:mb-1.5">
                                        <span
                                            className={`text-xs sm:text-sm ${
                                                isTodayDate
                                                    ? `flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-white sm:h-6 sm:w-6 sm:text-sm ${colors.primaryBg}`
                                                    : 'font-normal text-gray-900 dark:text-neutral-300'
                                            }`}
                                        >
                                            {day}
                                        </span>
                                    </div>

                                    <div className="space-y-0.5 sm:space-y-1">
                                        {dayEvents.map((event) => (
                                            <EventCard
                                                key={event.id}
                                                event={event}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarGrid;
